import { Controller } from "@hotwired/stimulus"
import { SlickGrid } from "slickgrid"
import Plotly from "plotly.js-dist"

export default class extends Controller {
  static targets = ["curveChart", "date1Display", "date1Input", "date2Display", "date2Input", "date3Display", "date3Input", "date4Display", "date4Input", "standardizedResidualChart", "zScoreChart", "curveDataTable", "curveTable", "resetGraphButton", "showLabelButton"]

  connect() {
    this.cachedData = {}
    this.curveDataGrid = null
    this.curveGrid = null
    this.indicesData = []
    this.isRendered = false
    this.colors = ['#0d1f2d', '#e66b3f', '#006d77', '#546a7b']
    this.initialize()
  }
  disconnect() {
    this.indicesData = []
    this.cachedData = {}
  }

  getXAxisLabel() {
    const labels = { 'years_to_maturity': 'Years to Maturity', 'mod_duration': 'Modified Duration', 'credit_duration': 'Credit Duration' }
    return labels[document.querySelector('input[name="x_axis"]')?.value] || 'Years to Maturity'
  }

  getYAxisLabel() {
    const labels = { 'i_spread': 'I Spread', 'yield': 'Yield', 'running_yield': 'Running Yield', 'margin': 'Margin', 'dts': 'DTS (Duration Times Spread)', 'g_spread': 'G Spread' }
    return labels[document.querySelector('input[name="y_axis"]')?.value] || 'I Spread'
  }

  getCurveLabel(optimal_method) {
    const labels = { 'ns': 'Nelson Siegel', 'nss': 'Nelson Siegel Svensson', 'nssc': 'Nelson Siegel Svensson Constrained', 'log': 'Log' }
    return labels[optimal_method] || 'Nelson Siegel'
  }
  
  getCurveChartLayout(){
    return {
      className: 'custom_graph',
      xaxis: { title: this.getXAxisLabel(), zeroline:true },
      yaxis: { title: this.getYAxisLabel(), zeroline:true, zerolinecolor: 'gray', zerolinewidth: 1, type: 'linear', tickformat: '.1%' },
      margin: { l: 55, b: 0, t: 10, r: 0 },
      hovermode: 'closest',
      legend: { orientation: 'h', y: -0.2 },
      responsive: true,
      autosize: true,
      config:{
        responsive: true,
        autosize: true
      }
    }
  }

  initializeCurveChart() {
    if (typeof Highcharts === 'undefined') {
      console.error('Highcharts is not loaded')
      return
    }
    
      Highcharts.chart(this.curveChartTarget, {
        chart: { type: 'scatter' },
        title: { 
          text: 'No data available', 
          style: { fontSize: '18px' },
          align: 'center',
          verticalAlign: 'middle',
          y: 0
        },
        xAxis: { title: { text: this.getXAxisLabel() } },
        yAxis: { title: { text: this.getYAxisLabel() } },
        series: [],
        credits: { enabled: false },
        legend: { enabled: false }
      })
  }

  initializeStandardizedResidualChart() {
    this.renderEmptyChart(this.standardizedResidualChartTarget)
  }

  initializeZScoreChart() {
    this.renderEmptyChart(this.zScoreChartTarget)
  }

  initializeDates() {
    const today = new Date().toISOString().split('T')[0]
    for (let i = 1; i <= 4; i++) {
      const dateInput = this[`date${i}InputTarget`]
      const dateDisplay = this[`date${i}DisplayTarget`]
      if (dateInput) {
        dateInput.value = today
      }
      if (dateDisplay) {
        dateDisplay.textContent = today
      }
      this.updateButtonVariants(i, 'today')
    }
  }

  initializeCurveDataTable() {
    this.curveDataGrid = new SlickGrid(this.curveDataTableTarget, [], this.getCurveDataTableColumns())
  }

  initializeCurveTable() {
    this.curveGrid = new SlickGrid(this.curveTableTarget, [], this.getCurveTableColumns())
  }

  renderEmptyChart(target) {
    Plotly.newPlot(target, [], {
      xaxis: { showgrid: false, showticklabels: false, zeroline: false },
      yaxis: { showgrid: false, showticklabels: false, zeroline: false },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      margin: { l: 50, b: 30, t: 30, r: 50 },
      height: 400
    }, { displayModeBar: false, responsive: true ,autosize: true})
  }

  updateButtonVariants(indexNumber, activeType) {

    const container = document.querySelector(`[data-pages--quant--curve-rvs-target="date${indexNumber}Display"]`).closest('.border')
    container.querySelectorAll('button[data-date-type]').forEach(button => {
      const isActive = button.dataset.dateType === activeType
      button.classList.toggle('bg-primary', !isActive)
      button.classList.toggle('bg-transparent', isActive)
      button.classList.toggle('border-primary', isActive)
    })
  }

  async loadIndices() {
    this.showLoading('controls')
    try {
      const response = await fetch('/quant/curve_rv/indices')
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const indices = await response.json()
      const sortedIndices = indices.sort((a, b) => a.name.localeCompare(b.name))
      this.indicesData = sortedIndices
      for (let i = 1; i <= 4; i++) this.updateIndexDropdown(`index_${i}`, sortedIndices)
    } catch (error) {
      console.error("Error loading indices:", error)
      for (let i = 1; i <= 4; i++) this.updateIndexDropdown(`index_${i}`, [])
    } finally {
      this.hideLoading('controls')
    }
  }

  updateIndexDropdown(selectId, options) {
    const selectedIndices = this.getSelectedIndices()
    const useableOptions = options?.filter(option => !selectedIndices.includes(option.id))
    const optionsList = document.querySelector(`#${selectId}-button`)?.closest('[data-controller="uikit--select-single"]')?.querySelector('[data-uikit--select-single-target="optionsList"]')
    if (!optionsList) return
    optionsList.innerHTML = useableOptions.map(option => `
      <div class="option-item flex items-center px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-600 cursor-pointer" 
           data-uikit--select-single-target="option" data-value="${option.id}" data-label="${option.name}" 
           data-action="click->uikit--select-single#selectOption">
        <input type="radio" name="${selectId}_radio" class="w-4 h-4 text-primary-600 bg-neutral-100 border-neutral-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-neutral-800 focus:ring-2 dark:bg-neutral-600 dark:border-neutral-500" 
               data-uikit--select-single-target="radio" data-action="click->uikit--select-single#selectOption click->uikit--select-single#preventPropagation">
        <label class="ml-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer flex-1">${option.name}</label>
      </div>
    `).join('')
  }

  updateIndex1(event) { this.updateIndex(1, event) }
  updateIndex2(event) { this.updateIndex(2, event) }
  updateIndex3(event) { this.updateIndex(3, event) }
  updateIndex4(event) { this.updateIndex(4, event) }
  updateIndex(indexNumber, event) { 
  if (this.updateChartTimeout) {
    clearTimeout(this.updateChartTimeout)
  }
  this.updateChartTimeout = setTimeout(() => this.updateChart(), 100)
  this.updateChart()
  }

  selectDate1(event) { this.selectDate(1, event) }
  selectDate2(event) { this.selectDate(2, event) }
  selectDate3(event) { this.selectDate(3, event) }
  selectDate4(event) { this.selectDate(4, event) }

  selectDate(indexNumber, event) {
    event.preventDefault()
    event.stopPropagation()
    
    const dateType = event.currentTarget.dataset.dateType
    const dateInput = this[`date${indexNumber}InputTarget`]
    const dateDisplay = this[`date${indexNumber}DisplayTarget`]

    if (!dateInput || !dateDisplay) return

    if (dateType === 'custom') {
      dateInput.classList.remove('hidden')
      dateInput.focus()
      dateInput.addEventListener('change', () => {
        const selectedDate = dateInput.value
        if (selectedDate) {
          dateDisplay.textContent = selectedDate
          dateInput.classList.add('hidden')
          this.updateButtonVariants(indexNumber, 'custom')
          this.updateChart()
        }
      })

      return
    }
    const today = new Date()
    const selectedDate = {
      'today': today,
      '1w': new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000),
      '1m': new Date(new Date(today).setMonth(today.getMonth() - 1)),
      '1y': new Date(new Date(today).setFullYear(today.getFullYear() - 1))
    }[dateType] || today
    const dateString = selectedDate.toISOString().split('T')[0]
    dateInput.value = dateString
    dateDisplay.textContent = dateString
    dateInput.classList.add('hidden')
    
    this.updateButtonVariants(indexNumber, dateType)
    if (this.updateChartTimeout) {
      clearTimeout(this.updateChartTimeout)
    }
    this.updateChartTimeout = setTimeout(() => this.updateChart(), 100)
  }

  resetGraph() {
    this.updateChart()

  }

  toggleLabels() {
    if (!Plotly) return
    
    const chartData = this.curveChartTarget.data
    if (!chartData || chartData.length === 0) return
    
    const bondTraces = []
    for (let i = 0; i < chartData.length; i++) {
      const trace = chartData[i]
      if (trace.mode === 'markers' || trace.mode === 'markers+text') {
        bondTraces.push(i)
      }
    }
    
    if (bondTraces.length === 0) return
    
    const firstBondTrace = bondTraces[0]
    const currentMode = chartData[firstBondTrace].mode || 'markers'
    const labelsVisible = currentMode.includes('text')
    const newMode = labelsVisible ? 'markers' : 'markers+text'
    
    Plotly.restyle(this.curveChartTarget, {
      mode: bondTraces.map(() => newMode)
    }, bondTraces)
    
    const button = document.querySelector('[data-action*="toggleLabels"]')
    if (button) {
      button.textContent = labelsVisible ? 'Show Labels' : 'Hide Labels'
    }
  }

  getCurveDataTableColumns() {
    return [
      { id: 'isin', name: 'ISIN', field: 'isin', minWidth: 140 }, 
      { id: 'name', name: 'Name', field: 'name', minWidth: 220 },
      { id: 'asx', name: 'ASX', field: 'asx', minWidth: 60 }, 
      { id: 'ttm', name: 'TTM', field: 'ttm', minWidth: 60 },
      { id: 'nssc', name: 'NSSC', field: 'nssc', minWidth: 80 }, 
      { id: 'nss', name: 'NSS', field: 'nss', minWidth: 80 },
      { id: 'ns', name: 'NS', field: 'ns', minWidth: 80 }, 
      { id: 'log', name: 'Log', field: 'log', minWidth: 80 },
      { id: 'values', name: 'Values', field: 'values', minWidth: 80 }, 
      { id: 'residual', name: 'Residual', field: 'residual', minWidth: 80 },
      { id: 'entityName', name: 'Entity Name', field: 'name', minWidth: 220 }, 
      { id: 'margin', name: 'Margin', field: 'margin', minWidth: 80 }, 
      { id: 'yield', name: 'Yield', field: 'yield', minWidth: 80 }, 
      { id: 'dts', name: 'DTS', field: 'dts', minWidth: 80 },
      { id: 'running_yield', name: 'Running Yield', field: 'running_yield', minWidth: 80 },
      { id: 'i_spread', name: 'I Spread', field: 'i_spread', minWidth: 80 },
      { id: 'g_spread', name: 'G Spread', field: 'g_spread', minWidth: 80 },
      { id: 'credit_duration', name: 'Credit Duration', field: 'credit_duration', minWidth: 80 },
      { id: 'mod_duration', name: 'Mod Duration', field: 'mod_duration', minWidth: 80 },
      { id: 'standardized_residual', name: 'Standardized Residual', field: 'standardized_residual', minWidth: 80 },
      { id: 'z_score', name: 'Z Score', field: 'z_score', minWidth: 80 },
    ]
  }

  getCurveTableColumns() {
    const baseColumns = [
      { id: 'ttc', name: 'TTC', field: 'ttc', minWidth: 80 }
    ]
    
    const curveTypes = ['ns', 'nss', 'nssc', 'log']
    
   for (let i = 0; i < 4; i++) {
    const indexId = document.querySelector(`input[name="index_${i+1}"]`)?.value
    if (!indexId) continue
      curveTypes.forEach(curveType => {
        baseColumns.push({
          id: `${curveType}_${i + 1}`,
          name: `Curve ${i + 1} (bps) ${curveType}`,
          field: `${curveType}_${i + 1}`,
          minWidth: 220
        })
      })
    }
    
    return baseColumns
  }

  createSlickGrid(target, data, columns, options = {}) {
    const defaultOptions = { 
      rowHeight: 32, 
      forceFitColumns: true,
      enableColumnReorder: true,
      enableTextSelectionOnCells: true,
      enableCellNavigation: true,
      autoEdit: false
    }
    const grid = new SlickGrid(target, data, columns, { ...defaultOptions, ...options })
    const style = document.createElement('style')
    style.textContent = `
      .slick-cell {
        display: flex !important;
        align-items: center !important;
        vertical-align: middle !important;
        line-height: 1 !important;
      }
      .slick-cell input {
        margin: 0 !important;
        vertical-align: middle !important;
      }
    `
    document.head.appendChild(style)
    setTimeout(() => { grid.resizeCanvas() }, 100)
    return grid
  }


  getSelectedIndices() {
    return Array.from({length: 4}, (_, i) => i + 1)
      .map(i => document.querySelector(`input[name="index_${i}"]`)?.value)
      .filter(Boolean)
  }

  getIndexName(indexId) {
    return indexId === 'CUSTOM' ? 'Custom' : this.indicesData?.find(index => index.id === indexId)?.name || null
  }

  showLoading(cardType) {
    const card = document.querySelector(`[data-loading-target="${cardType}"]`)
    if (card) {
      const spinner = card.querySelector('.loading-spinner')
      if (spinner) spinner.classList.remove('hidden')
    }
  }

  hideLoading(cardType) {
    const card = document.querySelector(`[data-loading-target="${cardType}"]`)
    if (card) {
      const spinner = card.querySelector('.loading-spinner')
      if (spinner) spinner.classList.add('hidden')
    }
  }

  disableButtons() {
    if (this.hasResetGraphButtonTarget) {
      this.resetGraphButtonTarget.disabled = true
    } else {
    }
    if (this.hasShowLabelButtonTarget) {
      this.showLabelButtonTarget.disabled = true
    } else {
    }
  }

  enableButtons() {
    if (this.hasResetGraphButtonTarget) {
      this.resetGraphButtonTarget.disabled = false
    } else {
    }
    if (this.hasShowLabelButtonTarget) {
      this.showLabelButtonTarget.disabled = false
    } else {
    }
  }

  handleChartClick(data) {
    if (data.points && data.points.length > 0) {
      const point = data.points[0]
      if (point.customdata){
        const isin = point.customdata
        if (isin) {
        const linkUrl = `/securities/${isin}`
          window.open(linkUrl, '_blank')
        }
      }
    }
  }

  async drawCurveChart(target, data) {
    if (typeof Highcharts === 'undefined') {
      console.error('Highcharts is not loaded')
      return
    }
    
    const series = data.map(trace => {
      const seriesConfig = {
        name: trace.name,
        data: trace.x.map((x, i) => ({
          x: x,
          y: trace.y[i],
          name: trace.name,
          isin: trace.customdata ? trace.customdata[i] : null,
          text: trace.text ? trace.text[i] : null
        })),
        type: trace.mode === 'lines' ? 'spline' : 'scatter',
        color: trace.marker?.color || trace.line?.color,
      }
      
      if (trace.mode === 'markers') {
        seriesConfig.marker = { 
          symbol: 'diamond', 
          radius: 6,
          lineWidth: 2,
          lineColor: '#001A4B'
        }
      } else if (trace.mode === 'lines') {
        seriesConfig.marker = { enabled: false }
        seriesConfig.lineWidth = 3
      }
      
      return seriesConfig
    })

    Highcharts.chart(target, {
      chart: { 
        type: 'scatter',
        backgroundColor: 'white'
      },
      title: { text: '' },
      xAxis: { 
        title: { 
          text: this.getXAxisLabel(),
          style: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151'
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#e5e7eb',
        lineWidth: 1,
        lineColor: '#d1d5db',
        labels: {
          style: {
            fontSize: '12px',
            color: '#6b7280'
          }
        }
      },
      yAxis: { 
        title: { 
          text: this.getYAxisLabel(),
          style: {
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151'
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#e5e7eb',
        lineWidth: 1,
        lineColor: '#d1d5db',
        labels: {
          style: {
            fontSize: '12px',
            color: '#6b7280'
          },
          formatter: function() {
            return (this.value * 100).toFixed(1) + '%';
          }
        }
      },
      tooltip: {
        useHTML: true,
        borderRadius: 8,
        shadow: {
          color: 'rgba(0, 0, 0, 0.1)',
          offsetX: 0,
          offsetY: 4,
          opacity: 0.1,
          width: 3
        },
        formatter: function() {
          const xAxisTitle = this.series.chart.userOptions.xAxis.title.text
          const yAxisTitle = this.series.chart.userOptions.yAxis.title.text
          
          if (this.series.type === 'scatter') {
            return `<b>Bond: ${this.point.text}</b><br><br>${xAxisTitle}: ${this.x.toFixed(2)}<br>${yAxisTitle}: ${(this.y * 100).toFixed(1)}%<br>`
          } else {
            return `${xAxisTitle}: ${this.x.toFixed(2)}<br>${yAxisTitle}: ${(this.y * 100).toFixed(1)}%<br>`
          }
        }
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        itemStyle: {
          fontSize: '12px',
          fontWeight: '500',
          color: '#374151'
        }
      },
      series: series,
      credits: { enabled: false },
      plotOptions: {
        scatter: {
          cursor: 'pointer',
          point: {
            events: {
              click: function(event) {
                if (this.isin) {
                  window.open(`/securities/${this.isin}`, '_blank')
                }
              }
            }
          }
        },
        spline: {
          cursor: 'pointer',
          point: {
            events: {
              click: function(event) {
                if (this.isin) {
                  window.open(`/securities/${this.isin}`, '_blank')
                }
              }
            }
          }
        }
      }
    })
  }

  getQueryString(indexId, date) {
    const xAxis = document.querySelector('input[name="x_axis"]')?.value || 'years_to_maturity'
    const yAxis = document.querySelector('input[name="y_axis"]')?.value || 'i_spread'
    const curve = document.querySelector('input[name="curve"]')?.value || 'optimal'
    return new URLSearchParams({
      id: indexId,
      date: date,
      x_axis: xAxis,
      y_axis: yAxis,
      curve: curve
    }).toString()
  }

  async fetchChartData(indexId, indexNumber = null) {
    const dateInputName = indexNumber ? `date_${indexNumber}` : 'date'
    const date = document.querySelector(`input[name="${dateInputName}"]`)?.value || new Date().toISOString().split('T')[0]
    const queryString = this.getQueryString(indexId, date)
    try {
      const response = await fetch(`/quant/curve_rv/chart_data?${queryString}`)
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const responseData = await response.json()
      this.cachedData[queryString] = responseData
      return responseData
    } catch (error) {
      console.error(`Error fetching chart data for index ${indexId}:`, error)
      throw error
    }
  }

  makeUniqueArray(array) {
    return Array.from(new Set(array.map(item => JSON.stringify(item))))
      .map(str => JSON.parse(str))
  }

  async updateChart(){
    const xAxis = document.querySelector('input[name="x_axis"]')?.value || 'years_to_maturity'
    const chartData = []
    const s_z_data = []
    const totalCurveData = []
    const selectedIndices = this.getSelectedIndices()
    if (selectedIndices.length === 0){
      this.initializeCurveChart()
      this.initializeCurveDataTable()
      this.initializeCurveTable()
      this.enableButtons()
      return
    }
    try{
      this.disableButtons()
      this.showLoading("curve-chart")
      this.showLoading("standardized-residual")
      this.showLoading("z-score")
      
      for (let i = 0; i < 4; i++) {
        const indexId = document.querySelector(`input[name="index_${i+1}"]`)?.value
        if (!indexId) continue
        const data = await this.fetchChartData(indexId, i + 1)
        const realData = data.data.filter(d => d.isin !==null)
        if (this.isRendered === false) {
          totalCurveData.push(...data.data)
          s_z_data.push(...realData)
          this.isRendered = true
          await this.updateStandardizedResidualChartFromData(this.makeUniqueArray(s_z_data))
          await this.updateZScoreChartFromData(this.makeUniqueArray(s_z_data))
          const uniqueCurveData = this.makeUniqueArray(totalCurveData)
          await this.updateCurveDataTable(uniqueCurveData)
        }
        const indexName = this.getIndexName(indexId) || `Index ${i + 1}`
        const color = this.colors[i % this.colors.length]
        if (realData.length > 0) {
          chartData.push({
            x: realData.map(d => d[xAxis]),
            y: realData.map(d => d.values),
            mode: 'markers',
            type: 'scatter',
            name: `Bonds - ${indexName}`,
            text: realData.map(d => d.name || d.isin),
            textposition: 'none',
            hovertemplate: `<b>Bond: %{text}</b><br><br>${this.getXAxisLabel()}: %{x:.2f}<br>${this.getYAxisLabel()}: %{y:.2%}<br><extra></extra>`,
            marker: { symbol: 'diamond', color, size: 10, line: { color: '#001A4B', width: 1 } },
            customdata: realData.map(d => d.isin)
          })
        }
        if (data.optimal_method !== undefined && data.data.length > 0) {
          chartData.push({
            x: data.data.map(d => d[xAxis]),
            y: data.data.map(d => d[data.optimal_method]),
            mode: 'lines',
            type: 'scatter',
            name: `${this.getCurveLabel(data.optimal_method)} - ${indexName}`,
            line: { shape: 'spline', smoothing: 0.7, color },
            hovertemplate: `${this.getXAxisLabel()}: %{x:.2f}<br>${this.getYAxisLabel()}: %{y:.2%}<br><extra></extra>`
          })
        }
      }

      await this.drawCurveChart(this.curveChartTarget, chartData)
      await this.updateCurveTable()


    }catch(error){
      console.error("Error updating chart:", error)
      await this.drawCurveChart(this.curveChartTarget, chartData)
    }finally{
      this.hideLoading("curve-chart")
      this.hideLoading("standardized-residual")
      this.hideLoading("z-score")
      this.enableButtons()
    }
  }

  async updateStandardizedResidualChartFromData(chartData) {
    try {
      if (!chartData?.length) return this.initializeStandardizedResidualChart()
      
      const data = chartData.filter(item => item.isin)
      const names = data.map(item => item.asx ? `${item.name} (${item.asx})` : item.name)
      const standardizedResiduals = data.map(item => item.standardized_residual || 0).sort((a, b) => a.standardized_residual - b.standardized_residual)
      
      const minValue = Math.min(...standardizedResiduals)
      const maxValue = Math.max(...standardizedResiduals)

      Plotly.newPlot(this.standardizedResidualChartTarget, [{
        x: standardizedResiduals,
        y: names,
        type: 'bar',
        orientation: 'h',
        marker: { color: '#0d1f2d' },
        name: 'Standardized Residual'
      }], {
        title: '',
        xaxis: { 
          title: '',
          range: [minValue - 2, maxValue + 2]
        },
        margin: { l: 250, b: 30, t: 10, r: 0 },
        height: 400,
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        shapes: [
          {
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: minValue - 2, 
            x1: -2.5,
            y0: 0,
            y1: 1,
            line: { width: 0 },
            layer: 'below'
          },
          {
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: 2.5,
            x1: maxValue + 2,
            y0: 0,
            y1: 1,
            line: { width: 0 },
            layer: 'below'
          }
        ]
      }, { displayModeBar: false, responsive: true ,autosize: true})
    } catch (error) {
      console.error("Error updating standardized residual chart:", error)
      this.initializeStandardizedResidualChart()
    }
  }

  async updateZScoreChartFromData(chartData) {
    try {
      if (!chartData?.length) return this.initializeZScoreChart()
      
      const data = chartData
        .filter(item => item.isin && item.z_score !== undefined)
        .map(item => ({ name: item.name, z_score: Math.round(item.z_score * 100) / 100, isin: item.isin }))
        .sort((a, b) => b.name - a.name)
      
      if (!data.length) return this.initializeZScoreChart()
      
      const zScores = data.map(item => item.z_score)
      const minValue = Math.min(...zScores)
      const maxValue = Math.max(...zScores)
      
      Plotly.newPlot(this.zScoreChartTarget, [{
        x: data.map(item => item.z_score),
        y: data.map(item => item.name),
        type: 'bar',
        orientation: 'h',
        marker: { color: '#0d1f2d' },
        name: 'Z - Scores',
        customdata: data.map(item => item.isin)
      }], {
        title: '',
        xaxis: { 
          title: '',
          range: [minValue - 2, maxValue + 2]
        },
        margin: { l: 250, b: 30, t: 10, r: 0 },
        height: 400,
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        shapes: [
          {
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: minValue - 2,
            x1: -2.5,
            y0: 0,
            y1: 1,
            line: { width: 0 },
            layer: 'below'
          },
          {
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: 2.5,
            x1: maxValue + 2,
            y0: 0,
            y1: 1,
            line: { width: 0 },
            layer: 'below'
          }
        ]
      }, { displayModeBar: false, responsive: true ,autosize: true})
    } catch (error) {
      this.initializeZScoreChart()
    }
  }

  updateCurveDataTable(chartData) {
    if (!chartData?.length) return
    const data = chartData.map(item => ({
      isin: item.isin? item.isin : '-',
      name: item.name? item.name : '-',
      asx: item.asx? item.asx : '-',
      ttm: item.years_to_maturity ? Number(item.years_to_maturity).toFixed(4) : '-',
      nssc: item.nssc ? Number(item.nssc).toFixed(6) : '-',
      nss: item.nss ? Number(item.nss).toFixed(6) : '-',
      ns: item.ns ? Number(item.ns).toFixed(6) : '-',
      log: item.log ? Number(item.log).toFixed(6) : '-',
      values: item.values ? Number(item.values).toFixed(6) : '-',
      residual: item.residual ? Number(item.residual).toFixed(6) : '-',
      accrued: item.accrued ? Number(item.accrued).toFixed(6) : '-',
      runningYield: item.running_yield ? Number(item.running_yield).toFixed(6) : '-',
      margin: item.margin ? Number(item.margin).toFixed(6) : '-',
      yield: item.yield ? Number(item.yield).toFixed(6) : '-',
      dts: item.dts ? Number(item.dts).toFixed(6) : '-', modDur: item.mod_duration ? Number(item.mod_duration).toFixed(4) : '-',
      credDur: item.credit_duration ? Number(item.credit_duration).toFixed(4) : '-',
      standardizedResidual: item.standardized_residual ? Number(item.standardized_residual).toFixed(6) : '-',
      currency: item.currency || '-', zScore: item.z_score ? Number(item.z_score).toFixed(6) : '-',
      running_yield: item.running_yield ? Number(item.running_yield).toFixed(6) : '-',
      i_spread: item.i_spread ? Number(item.i_spread).toFixed(6) : '-',
      g_spread: item.g_spread ? Number(item.g_spread).toFixed(6) : '-',
      credit_duration: item.credit_duration ? Number(item.credit_duration).toFixed(4) : '-',
      mod_duration: item.mod_duration ? Number(item.mod_duration).toFixed(4) : '-',
      standardized_residual: item.standardized_residual ? Number(item.standardized_residual).toFixed(6) : '-',
      z_score: item.z_score ? Number(item.z_score).toFixed(6) : '-',
    }))

    if (this.curveDataGrid) this.curveDataGrid.destroy()
    this.curveDataGrid = this.createSlickGrid(this.curveDataTableTarget, data, this.getCurveDataTableColumns())
  }

  updateCurveTable() {
    const allData = []

    for (let i = 0; i < 4; i++) {
      const indexId = document.querySelector(`input[name="index_${i+1}"]`)?.value
      if (!indexId) continue
      const date = document.querySelector(`input[name="date_${i+1}"]`)?.value
      const queryString = this.getQueryString(indexId, date)
      const cachedData = this.cachedData[queryString]
      if (!cachedData) continue
      const filteredData = cachedData.data.filter(item => {
        const ttc = Number(item.years_to_maturity)
        return ttc % 0.5 === 0 && ttc >= 0
      })
      
      const indexData = filteredData.map(item => ({
        ttc: item.years_to_maturity,
        [`ns_${i+1}`]: item.ns ? Math.round(Number(item.ns) * 10000) : '-',
        [`nss_${i+1}`]: item.nss ? Math.round(Number(item.nss) * 10000) : '-',
        [`nssc_${i+1}`]: item.nssc ? Math.round(Number(item.nssc) * 10000) : '-',
        [`log_${i+1}`]: item.log ? Math.round(Number(item.log) * 10000) : '-'
      }))
      allData.push(indexData)
    }
    const mergedData = {}
    allData.forEach(indexData => {
      indexData.forEach(item => {
        const ttc = item.ttc
        if (!mergedData[ttc]) {
          mergedData[ttc] = { ttc: ttc }
        }
        Object.assign(mergedData[ttc], item)
      })
    })

    const data = Object.values(mergedData).sort((a, b) => a.ttc - b.ttc)
    if (this.curveGrid) this.curveGrid.destroy()
    this.curveGrid = this.createSlickGrid(this.curveTableTarget, data, this.getCurveTableColumns())
  }

 initialize() {
  this.initializeDates()
  this.initializeCurveChart()
  this.initializeZScoreChart()
  this.initializeStandardizedResidualChart()
  this.initializeCurveDataTable()
  this.initializeCurveTable()
  this.loadIndices()
  }
}
